import { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDocumentStore } from '@/store/documentStore';
import { useEditorStore } from '@/store/editorStore';
import { commandRegistry } from '@/commands/registry';
import { usePlanGating } from '@/hooks/usePlanGating';
import type { CommandContext } from '@/commands/types';

interface ExportOption {
  commandId: string;
  labelKey: string;
  extension: string;
  /** Which plan capability gates this option, if any. */
  gateKey?: 'canExportPdf' | 'canExportPng';
}

const EXPORT_OPTIONS: ExportOption[] = [
  { commandId: 'export.markdown', labelKey: 'dialogs.markdown', extension: '.md' },
  { commandId: 'export.textOutline', labelKey: 'dialogs.textOutline', extension: '.txt' },
  { commandId: 'export.svg', labelKey: 'dialogs.svgImage', extension: '.svg' },
  { commandId: 'export.png', labelKey: 'dialogs.pngImage', extension: '.png', gateKey: 'canExportPng' },
  { commandId: 'export.pdf', labelKey: 'dialogs.pdfDocument', extension: '.pdf', gateKey: 'canExportPdf' },
  { commandId: 'export.json', labelKey: 'dialogs.jsonNative', extension: '.json' },
];

function getContext(): CommandContext {
  return {
    doc: useDocumentStore.getState().mapDoc,
    editorState: useEditorStore.getState(),
  };
}

export function ExportMenu() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const gating = usePlanGating();

  // Close on outside click
  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
        setShowUpgrade(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        setShowUpgrade(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open]);

  const handleExport = useCallback((option: ExportOption) => {
    // Check plan gating
    if (option.gateKey) {
      const allowed = gating[option.gateKey];
      if (!allowed) {
        setShowUpgrade(true);
        return;
      }
    }
    commandRegistry.execute(option.commandId, getContext());
    setOpen(false);
  }, [gating]);

  const isLocked = (option: ExportOption): boolean => {
    if (!option.gateKey) return false;
    return !gating[option.gateKey];
  };

  return (
    <div ref={menuRef} style={{ position: 'relative' }}>
      <button
        onClick={() => {
          setOpen((prev) => !prev);
          setShowUpgrade(false);
        }}
        style={{
          background: 'var(--bg-surface-raised)',
          color: 'var(--text-primary)',
          border: '1px solid var(--border-strong)',
          borderRadius: 6,
          padding: '6px 12px',
          cursor: 'pointer',
          fontSize: 'var(--font-size-sm)',
          fontFamily: 'var(--font-family)',
        }}
      >
        {t('dialogs.export')}
      </button>

      {open && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            right: 0,
            marginTop: 4,
            background: 'var(--bg-surface)',
            border: '1px solid var(--border-default)',
            borderRadius: 'var(--radius-md)',
            padding: '4px 0',
            minWidth: 220,
            zIndex: 20,
            boxShadow: 'var(--shadow-md)',
          }}
        >
          {showUpgrade ? (
            <div style={{ padding: '12px 16px' }}>
              <p style={{
                margin: '0 0 8px',
                fontSize: 'var(--font-size-sm)',
                color: 'var(--text-primary)',
                fontFamily: 'var(--font-family)',
              }}>
                {t('dialogs.proRequired')}
              </p>
              <a
                href="/pricing"
                style={{
                  display: 'inline-block',
                  padding: '6px 14px',
                  background: 'var(--color-secondary)',
                  color: '#fff',
                  borderRadius: 6,
                  fontSize: 'var(--font-size-sm)',
                  fontFamily: 'var(--font-family)',
                  fontWeight: 500,
                  textDecoration: 'none',
                  cursor: 'pointer',
                }}
              >
                {t('dialogs.upgradeToPro')}
              </a>
            </div>
          ) : (
            EXPORT_OPTIONS.map((option) => {
              const locked = isLocked(option);
              return (
                <button
                  key={option.commandId}
                  onClick={() => handleExport(option)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    width: '100%',
                    padding: '8px 16px',
                    background: 'transparent',
                    border: 'none',
                    color: locked ? 'var(--text-tertiary)' : 'var(--text-primary)',
                    fontSize: 'var(--font-size-sm)',
                    fontFamily: 'var(--font-family)',
                    cursor: 'pointer',
                    textAlign: 'left',
                  }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLButtonElement).style.background = 'var(--bg-surface-raised)';
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
                  }}
                >
                  <span style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 6 }}>
                    {locked && (
                      <svg width="12" height="12" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0 }}>
                        <rect x="2" y="7" width="12" height="8" rx="2" fill="var(--text-tertiary)" />
                        <path d="M5 7V5a3 3 0 0 1 6 0v2" stroke="var(--text-tertiary)" strokeWidth="1.5" fill="none" />
                      </svg>
                    )}
                    {t(option.labelKey)}
                  </span>
                  {locked ? (
                    <span style={{
                      fontSize: 10,
                      fontWeight: 600,
                      background: 'var(--color-secondary)',
                      color: '#fff',
                      padding: '1px 6px',
                      borderRadius: 'var(--radius-sm)',
                      lineHeight: '16px',
                    }}>
                      {t('dialogs.pro')}
                    </span>
                  ) : (
                    <span style={{ color: 'var(--text-tertiary)', fontSize: 'var(--font-size-xs)' }}>{option.extension}</span>
                  )}
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
